import { onMounted, ref } from 'vue';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import md5 from 'md5';
import { useApiFieldErrors, useI18n, useTableQuery, useCurrencyList } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getCampaignApiRequest,
  postCampaignUpdateCostApiRequest,
} from '@/services/api/campaign-api';
import { useStore } from '@/store';

type UpdateCostForm = {
  timezone: string;
  restore: number | boolean;
  startTimestamp?: string;
  endTimestamp?: string;
  cost?: number;
  currencyId?: string;
};

const getDefaultForm = (defaultTimezone: string): UpdateCostForm => ({
  timezone: defaultTimezone,
  restore: 0,
});

export const useCampaignUpdateCost = (emitClose: () => void) => {
  const { t, locale } = useI18n();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const { currencyListOrderedPreferred, currencies } = useCurrencyList();
  const {    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const step = ref(1);
  const show = ref(false);
  const campaignId = ref('');
  const name = ref('');
  const form = ref<UpdateCostForm>(getDefaultForm(store.user.defaultTimezone));
  const oldHash = ref('');

  const timezoneList = store.timezoneList;

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const handleUpdatePeriod = (value: TimeRange) => {
    form.value.startTimestamp = value.from;
    form.value.endTimestamp = value.to;
  };

  const load = async () => {
    campaignId.value = store.getTableSelectedItem().id as string;

    const data = await getCampaignApiRequest(campaignId.value);
    const { to, from } = tableQueryApi.getTableQueryForCurrentTab();
    const now = moment();

    name.value = data.name;
    form.value = {
      ...getDefaultForm(store.user.defaultTimezone),
      startTimestamp: from || now.format('YYYY-MM-DD 00:00:00'),
      endTimestamp: to || now.format('YYYY-MM-DD 23:59:59'),
      cost: 0,
      currencyId: data.currencyId,
    };
  };

  const submit = async () => {
    clearErrors();

    const payload = cloneDeep(form.value);
    payload.restore = Number(form.value.restore);

    try {
      await postCampaignUpdateCostApiRequest(campaignId.value, payload);
      close();
    } catch (error) {
      applyApiErrors(error);
    }
  };

  onMounted(async () => {
    store.setLoading(true);

    try {
      await load();
      oldHash.value = getCurrentHash();
      show.value = true;
    } finally {
      store.setLoading(false);
    }
  });

  return {
    t,
    locale,
    step,
    show,
    name,
    form,
    timezoneList,
    currencyListOrderedPreferred,
    currencies,
    errors,
    errorsControlActive,
    errorsDict,
    checkClose,
    handleUpdatePeriod,
    submit,
    close,
  };
};
